import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import Alert from '@mui/material/Alert';
import { MaterialReactTable } from 'material-react-table';
import { format, formatDistance } from 'date-fns';

// Icons
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BugReportIcon from '@mui/icons-material/BugReport';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DescriptionIcon from '@mui/icons-material/Description';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FilterListIcon from '@mui/icons-material/FilterList';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {TableCell, TableContainer, TableHead, TableRow, TableBody, Table, TableSortLabel} from '@mui/material'
import AdminPageWrapper from './AdminPageWrapper';
import { PageHeader } from '../common/PageHeader';

const BugReportsAdmin = ({ darkMode, toggleDarkMode, themeVariant, embedded = false, onBugUpdated }) => {
  const [bugReports, setBugReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [selectedReport, setSelectedReport] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [resolution, setResolution] = useState('');
  const [resolveLoading, setResolveLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  
  // Add these with the other state variables
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const sampleBugReports = [
    {
      id: 1,
      title: 'Video playback stops after 30 seconds',
      description: 'When editing subtitles, the video stops playing after 30 seconds',
      submittedBy: 'test@example.com',
      timestamp: new Date().toISOString(),
      resolved: false,
      browser: 'Chrome 98.0.4758.102',
      stepsToReproduce: '1. Open any video\n2. Play the video\n3. Wait for 30 seconds'
    },
    {
      id: 2,
      title: 'Cannot save Hindi subtitles',
      description: 'When trying to save Hindi subtitles, getting an error',
      submittedBy: 'user2@example.com',
      timestamp: new Date(Date.now() - 86400000).toISOString(), // 1 day ago
      resolved: true,
      resolvedBy: 'admin@example.com',
      resolvedAt: new Date().toISOString(),
      resolution: 'Fixed encoding issue in the backend',
      browser: 'Firefox 97.0'
    }
  ];
  
  const fetchBugReports = async () => {
    setLoading(true);
    setError(null);

    try {
      const authUser = JSON.parse(localStorage.getItem('authUser'));
      const authToken = localStorage.getItem('authToken')?.replace(/"/g, '');
      
      console.log('Fetching bug reports...');
      // Make sure we're using the correct API endpoint
      const response = await axios.get(`${process.env.REACT_APP_API_URL || ''}/api/v1/bug-reports`, {
        headers: {
          'email': authUser?.email || '',
          'auth': authToken || ''
        }
      });
      
      console.log('API response received:', response.data);
      
      if (Array.isArray(response.data)) {
        setBugReports(normalizeReportData(response.data));
        console.log(`Successfully loaded ${response.data.length} bug reports`);
        
        // Don't show success message on initial load
        if (!loading) {
          setSnackbar({
            open: true,
            message: `Loaded ${response.data.length} bug reports successfully`,
            severity: 'info'
          });
        }
      } else {
        console.error('API response is not an array:', response.data);
        setBugReports(normalizeReportData(sampleBugReports));
        setError('Invalid response format from API');
      }
    } catch (err) {
      console.error('Error fetching bug reports:', err);
      setBugReports(normalizeReportData(sampleBugReports));
      setError('Failed to load bug reports. ' + (err.response?.data?.message || err.message));

      setSnackbar({
        open: true,
        message: 'Failed to refresh bug reports: ' + (err.response?.data?.message || err.message),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchBugReports();
  }, []);
  
  useEffect(() => {
    console.log('Bug reports loaded:', bugReports.length);
    console.log('Current tab value:', tabValue);
  }, [bugReports, tabValue]);

  const normalizeReportData = (reports) => {
    if (!Array.isArray(reports)) return [];
    
    return reports.map(report => ({
      ...report,
      // Ensure resolved is a boolean
      resolved: report.resolved === true || report.resolved === "true",
      // Ensure other critical fields exist
      id: report.id || Math.random().toString(36).substring(2, 11),
      title: report.title || 'Untitled Report',
      timestamp: report.timestamp || new Date().toISOString(),
    }));
  };

  const getFilteredReports = () => {
    if (!bugReports || !Array.isArray(bugReports) || bugReports.length === 0) {
      console.log('No bug reports available');
      return [];
    }
    
    // Deep copy to avoid issues with object references
    const reports = [...bugReports];
    
    console.log(`Getting reports for tab ${tabValue} (${tabValue === 0 ? 'open' : 'resolved'})`);
    console.log(`Total reports before filtering: ${reports.length}`);
    
    // Define more precise filtering that handles edge cases
    const filtered = tabValue === 0
      ? reports.filter(report => report && report.resolved === false)
      : reports.filter(report => report && report.resolved === true);
    
    console.log(`Filtered reports count: ${filtered.length}`);
    
    // Ensure all objects have the right properties
    return filtered.map(report => ({
      id: report.id,
      title: report.title || '',
      description: report.description || '',
      submittedBy: report.submittedBy || '',
      timestamp: report.timestamp || new Date().toISOString(),
      resolved: Boolean(report.resolved),
      resolvedBy: report.resolvedBy || '',
      resolvedAt: report.resolvedAt || '',
      resolution: report.resolution || '',
      browser: report.browser || '',
      stepsToReproduce: report.stepsToReproduce || '',
      logsJson: report.logsJson || '[]',
      systemInfo: report.systemInfo || null,
    }));
  };

  // Add this function after getFilteredReports

  // Debug function to check for valid data structure
  const validateTableData = () => {
    const data = getFilteredReports();
    
    if (!data || !Array.isArray(data)) {
      console.error('validateTableData: Data is not an array');
      return false;
    }
    
    if (data.length === 0) {
      console.log('validateTableData: Data array is empty (not an error)');
      return true;
    }
    
    // Check first record for required fields
    const firstItem = data[0];
    console.log('validateTableData: First item:', firstItem);
    
    const requiredFields = ['id', 'title', 'timestamp', 'resolved'];
    const missingFields = requiredFields.filter(field => 
      firstItem[field] === undefined || firstItem[field] === null
    );
    
    if (missingFields.length > 0) {
      console.error(`validateTableData: Missing required fields: ${missingFields.join(', ')}`);
      return false;
    }
    
    console.log('validateTableData: Data is valid');
    return true;
  };

  // Then call this function just before rendering the table
  useEffect(() => {
    if (bugReports.length > 0) {
      validateTableData();
    }
  }, [bugReports, tabValue]);
  
  const handleTabChange = (event, newValue) => {
    console.log(`Tab changing from ${tabValue} to ${newValue}`);
    setTabValue(newValue);
  };
  
  const handleViewReport = (report) => {
    console.log('Viewing report details:', report); // Debug log
    setSelectedReport(report);
    setViewDialogOpen(true);
  };
  
  const handleResolveClick = (report) => {
    setSelectedReport(report);
    setResolution('');
    setResolveDialogOpen(true);
  };
  
  const handleResolveSubmit = async () => {
    setResolveLoading(true);
    try {
      const authUser = JSON.parse(localStorage.getItem('authUser'));
      const authToken = localStorage.getItem('authToken')?.replace(/"/g, '');
      
      await axios.put(`${process.env.REACT_APP_API_URL || ''}/api/v1/bug-reports/${selectedReport.id}/resolve`, 
        { resolution },
        {
          headers: {
            'email': authUser.email,
            'auth': authToken
          }
        }
      );
      
      setResolveDialogOpen(false);
      setSnackbar({
        open: true,
        message: 'Bug report resolved successfully',
        severity: 'success'
      });
      fetchBugReports(); // Refresh the list
    } catch (err) {
      console.error('Error resolving bug report:', err);
      setSnackbar({
        open: true,
        message: 'Failed to resolve bug report: ' + (err.response?.data?.message || err.message),
        severity: 'error'
      });
    } finally {
      setResolveLoading(false);
    }
  };

  // Add this after the handleResolveSubmit function
const handleDeleteClick = (report) => {
    setSelectedReport(report);
    setDeleteDialogOpen(true);
  };
  
  const handleDeleteSubmit = async () => {
    setDeleteLoading(true);
    try {
      const authUser = JSON.parse(localStorage.getItem('authUser'));
      const authToken = localStorage.getItem('authToken')?.replace(/"/g, '');
      
      await axios.delete(`${process.env.REACT_APP_API_URL || ''}/api/v1/bug-reports/${selectedReport.id}`, {
        headers: {
          email: authUser.email,
          auth: authToken,
          'Access-Control-Allow-Origin': '*',
          'Access-Control-Allow-Credentials': true,
        }
      });
      
      setDeleteDialogOpen(false);
      setSnackbar({
        open: true,
        message: 'Bug report deleted successfully',
        severity: 'success'
      });
      fetchBugReports(); // Refresh the list
    } catch (err) {
      console.error('Error deleting bug report:', err);
      setSnackbar({
        open: true,
        message: 'Failed to delete bug report: ' + (err.response?.data?.message || err.message),
        severity: 'error'
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  // Add a helper function to the component to properly detect system info format
  const getSystemInfoType = (systemInfo) => {
    if (!systemInfo) return 'none';
    if (typeof systemInfo === 'string') return 'string';
    if (typeof systemInfo === 'object') return 'object';
    return 'unknown';
  };
  
  const handleSnackbarClose = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return format(date, 'PPpp'); // Format: Apr 29, 2023, 3:30 PM
    } catch (e) {
      return dateString;
    }
  };
  
  const formatTimeAgo = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return formatDistance(date, new Date(), { addSuffix: true });
    } catch (e) {
      return 'Unknown';
    }
  };

  // Replace your current columns definition with this improved version

  const columns = React.useMemo(() => [
    {
      accessorKey: 'id',
      header: 'ID',
      size: 70,
      enableSorting: true,
    },
    {
      accessorKey: 'title',
      header: 'Title',
      size: 250,
      Cell: ({ renderedCellValue, row }) => (
        <Tooltip title={row.original.description || ''}>
          <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>
            {renderedCellValue || 'Untitled'}
          </Typography>
        </Tooltip>
      ),
    },
    {
      accessorKey: 'submittedBy',
      header: 'Submitted By',
      size: 180,
      Cell: ({ renderedCellValue }) => (
        <Typography variant="body2">
          {renderedCellValue || 'Anonymous'}
        </Typography>
      ),
    },
    {
      accessorKey: 'timestamp',
      header: 'Date',
      size: 140,
      Cell: ({ row }) => (
        <Tooltip title={formatDate(row.original.timestamp)}>
          <Typography variant="body2">
            {formatTimeAgo(row.original.timestamp)}
          </Typography>
        </Tooltip>
      ),
      sortingFn: 'datetime',
    },
    {
      accessorKey: 'resolved',
      header: 'Status',
      size: 120,
      filterVariant: 'select',
      filterSelectOptions: [
        { text: 'Open', value: false },
        { text: 'Resolved', value: true }
      ],
      Cell: ({ row }) => (
        row.original.resolved ? 
        <Chip 
          label="Resolved" 
          color="success" 
          size="small" 
          icon={<CheckCircleIcon fontSize="small" />} 
        /> : 
        <Chip 
          label="Open" 
          color="error" 
          size="small" 
          icon={<BugReportIcon fontSize="small" />} 
        />
      ),
    }
  ], []);

  // Create the main content as a separate element
  const content = (
    <Box sx={{
      width: '100%',
      ...(embedded ? {} : {
        p: { xs: 1, sm: 1, md: 2 },
        backgroundColor: darkMode ? 'background.default' : '#f5f5f5',
        minHeight: '100vh',
        margin: '20px 20px 20px 20px',
      })
    }}>
      
      {/* Main content */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 2 
      }}>
        {!embedded && <Typography variant="h6">Bug Reports</Typography>}
        
        <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
          <Tooltip title="Refresh bug reports">
            <IconButton 
              color="primary"
              onClick={fetchBugReports}
              disabled={loading}
              size="small"
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            aria-label="bug report tabs"
            variant="standard"
            sx={{ minHeight: 'auto', borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab 
              label={<Box sx={{ display: 'flex', alignItems: 'center' }}>
                <BugReportIcon fontSize="small" sx={{ mr: 0.5 }} />
                Open
              </Box>} 
              value={0}
              sx={{ minHeight: 'auto', py: 1 }}
            />
            <Tab 
              label={<Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CheckCircleIcon fontSize="small" sx={{ mr: 0.5 }} />
                Resolved
              </Box>} 
              value={1}
              sx={{ minHeight: 'auto', py: 1 }}
            />
          </Tabs>
        </Box>
      </Box>
      
      {/* Table, same as before */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <Stack alignItems="center" spacing={2}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">Loading bug reports...</Typography>
          </Stack>
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      ) : (
        <MaterialReactTable
          columns={columns}
          data={getFilteredReports()}
          initialState={{
            density: 'compact',
            pagination: {
              pageSize: 10,
              pageIndex: 0
            },
            sorting: [{ id: 'timestamp', desc: true }]
          }}
          enableColumnFilterModes
          enableColumnOrdering
          enablePagination
          enableSorting
          enableBottomToolbar
          enableTopToolbar
          muiTableBodyProps={{
            sx: {
              '& tr:nth-of-type(odd)': {
                backgroundColor: theme => theme.palette.mode === 'dark' 
                  ? 'rgba(255, 255, 255, 0.05)' 
                  : 'rgba(0, 0, 0, 0.02)',
              },
            },
          }}
          muiTableHeadCellProps={{
            sx: {
              fontWeight: 'bold',
            },
          }}
          positionActionsColumn="last"
          enableRowActions
          displayColumnDefOptions={{
            'mrt-row-actions': {
              header: 'Actions',
              size: 240,
            }
          }}
          renderRowActions={({ row }) => {
            /* Keep existing row actions */
            const report = row.original;
            return (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button 
                  size="small"
                  variant="outlined" 
                  color="primary"
                  onClick={() => handleViewReport(report)}
                  startIcon={<VisibilityIcon />}
                  data-testid={`view-bug-${report.id}`}
                >
                  View
                </Button>
                
                {!report.resolved && (
                  <Button 
                    size="small"
                    variant="outlined" 
                    color="success"
                    onClick={() => handleResolveClick(report)}
                    startIcon={<CheckCircleIcon />}
                    data-testid={`resolve-bug-${report.id}`}
                  >
                    Resolve
                  </Button>
                )}
                
                <Button 
                  size="small"
                  variant="outlined" 
                  color="error"
                  onClick={() => handleDeleteClick(report)}
                  startIcon={<DeleteOutlineIcon />}
                  data-testid={`delete-bug-${report.id}`}
                >
                  Delete
                </Button>
              </Box>
            );
          }}
          renderEmptyRowsFallback={() => (
            /* Keep existing empty state */
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              justifyContent: 'center',
              p: 4
            }}>
              <BugReportIcon sx={{ fontSize: 60, color: 'text.secondary', opacity: 0.5, mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No {tabValue === 0 ? 'open' : 'resolved'} bug reports found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {tabValue === 0 
                  ? "When users report issues, they'll appear here." 
                  : "Resolved bug reports will appear here."}
              </Typography>
            </Box>
          )}
        />
      )}
    </Box>
  );

  // All dialogs and snackbars remain the same
  const dialogs = (
    <>
      {/* View Report Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
        data-testid="bug-detail-dialog"
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <BugReportIcon sx={{ mr: 1 }} />
          Bug Report Details
        </DialogTitle>
        <DialogContent dividers>
          {selectedReport && (
            <Box sx={{ mt: 1 }}>
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Typography variant="h6" gutterBottom>
                      {selectedReport.title}
                    </Typography>
                    <Box>
                      <Chip 
                        label={selectedReport.resolved ? 'Resolved' : 'Open'} 
                        color={selectedReport.resolved ? 'success' : 'error'}
                        size="small"
                      />
                    </Box>
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Reported {formatTimeAgo(selectedReport.timestamp)} • {formatDate(selectedReport.timestamp)}
                  </Typography>
                  
                  {/* Enhanced description display with JSON formatting */}
                  <Box sx={{ mb: 2 }}>
                    {(() => {
                      const description = selectedReport.description || 'No description provided';
                      
                      // Function to check if a string contains JSON
                      const containsJson = (str) => {
                        const jsonRegex = /(\{[\s\S]*\}|\[[\s\S]*\])/;
                        const match = str.match(jsonRegex);
                        if (!match) return false;
                        
                        try {
                          JSON.parse(match[0]);
                          return match[0];
                        } catch (e) {
                          return false;
                        }
                      };
                      
                      // Try to find and parse JSON in the description
                      const jsonContent = containsJson(description);
                      
                      if (jsonContent) {
                        // Split description into parts before, during, and after JSON
                        const beforeJson = description.substring(0, description.indexOf(jsonContent));
                        const afterJson = description.substring(description.indexOf(jsonContent) + jsonContent.length);
                        
                        // Format the JSON part
                        let formattedJson;
                        try {
                          const jsonObj = JSON.parse(jsonContent);
                          formattedJson = JSON.stringify(jsonObj, null, 2);
                        } catch (e) {
                          formattedJson = jsonContent;
                        }
                        
                        return (
                          <>
                            {beforeJson && (
                              <Typography variant="body1" paragraph>
                                {beforeJson}
                              </Typography>
                            )}
                            
                            <Typography variant="subtitle2" color="primary" gutterBottom>
                              Context Data:
                            </Typography>
                            
                            <Paper 
                              variant="outlined" 
                              sx={{ 
                                p: 2, 
                                mb: 2, 
                                backgroundColor: theme => 
                                  theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                                borderRadius: 1,
                                maxHeight: '300px',
                                overflow: 'auto'
                              }}
                            >
                              <pre style={{ 
                                margin: 0, 
                                fontFamily: 'monospace', 
                                fontSize: '0.85rem',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word'
                              }}>
                                {formattedJson}
                              </pre>
                            </Paper>
                            
                            {afterJson && (
                              <Typography variant="body1">
                                {afterJson}
                              </Typography>
                            )}
                          </>
                        );
                      } else {
                        // If no JSON found, display the description as regular text
                        return (
                          <Typography variant="body1">
                            {description}
                          </Typography>
                        );
                      }
                    })()}
                  </Box>
                  
                  {/* If contextData exists as a separate field, display it */}
                  {selectedReport.contextData && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle2" color="primary" gutterBottom>
                        Additional Context Data:
                      </Typography>
                      
                      <Paper 
                        variant="outlined" 
                        sx={{ 
                          p: 2, 
                          backgroundColor: theme => 
                            theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                          borderRadius: 1,
                          maxHeight: '300px',
                          overflow: 'auto'
                        }}
                      >
                        <pre style={{ 
                          margin: 0, 
                          fontFamily: 'monospace', 
                          fontSize: '0.85rem',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word'
                        }}>
                          {JSON.stringify(selectedReport.contextData, null, 2)}
                        </pre>
                      </Paper>
                    </Box>
                  )}
                </CardContent>
              </Card>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, mb: 3, height: '100%' }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Steps to Reproduce
                    </Typography>
                    <Typography variant="body2">
                      {selectedReport.stepsToReproduce || 'No steps provided'}
                    </Typography>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={6}>                    
                    {selectedReport.resolved && (
                      <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="subtitle2" color="success.main" gutterBottom>
                          <CheckCircleIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'text-bottom' }} />
                          Resolved Information
                        </Typography>
                        
                        <Stack spacing={1.5}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Resolved By:
                            </Typography>
                            <Typography variant="body2">
                              {selectedReport.resolvedBy || 'N/A'}
                            </Typography>
                          </Box>
                          
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Resolved At:
                            </Typography>
                            <Typography variant="body2">
                              {formatDate(selectedReport.resolvedAt)}
                            </Typography>
                          </Box>
                          
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Resolution:
                            </Typography>
                            <Typography variant="body2">
                              {selectedReport.resolution || 'No details provided'}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    )}
                </Grid>
              </Grid>
              
              {selectedReport.logsJson && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <DescriptionIcon fontSize="small" sx={{ mr: 1 }} />
                    Browser Logs
                  </Typography>
                  <Box
                    sx={{
                      maxHeight: '300px',
                      overflow: 'auto',
                      p: 1.5,
                      backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                      borderRadius: 1,
                      fontFamily: 'monospace',
                      fontSize: '0.8rem'
                    }}
                  >
                    {(() => {
                      try {
                        const logs = JSON.parse(selectedReport.logsJson);
                        return logs.map((log, index) => (
                          <Box key={index} sx={{ mb: 1 }}>
                            <Typography variant="caption" color={`${log.level === 'error' ? 'error' : 'text.secondary'}`}>
                              [{log.timestamp || new Date().toISOString()}] {log.level?.toUpperCase()}:
                            </Typography>
                            <Typography variant="body2">
                              {Array.isArray(log.message) 
                                ? log.message.join(' ') 
                                : log.message}
                            </Typography>
                          </Box>
                        ));
                      } catch (error) {
                        return (
                          <Typography variant="body2" color="error">
                            Error parsing logs: {error.message}
                          </Typography>
                        );
                      }
                    })()}
                  </Box>
                </Paper>
              )}
              
              {/* System Information section - unified display */}
              {selectedReport && selectedReport.systemInfo && (
                <Box sx={{ mt: 3 }}>
                  
                  
                  
                  <Paper 
                      variant="outlined"
                      sx={{ 
                        p: 1.5,
                        backgroundColor: theme => 
                          theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                        borderRadius: 1,
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        whiteSpace: 'pre-wrap'
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                        <DescriptionIcon fontSize="small" sx={{ mr: 1 }} />
                        Technical Information:
                      </Typography>
                      <Stack spacing={1}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          System Information:
                        </Typography>
                        <Typography variant="body2">
                          {selectedReport.systemInfo.trim() || 'No system information provided'}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Browser:
                        </Typography>
                        <Typography variant="body2">
                          {selectedReport.browser || 'Unknown'}
                        </Typography>
                      </Box>
                      
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Submitted By:
                        </Typography>
                        <Typography variant="body2">
                          {selectedReport.submittedBy || 'Anonymous'}
                        </Typography>
                      </Box>
                    </Stack>
                    </Paper>
                  
                      
                
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
          <DialogActions>
          {selectedReport && !selectedReport.resolved && (
              <Button 
              onClick={() => {
                  setViewDialogOpen(false);
                  handleResolveClick(selectedReport);
              }}
              color="success"
              variant="contained"
              startIcon={<CheckCircleIcon />}
              data-testid="detail-resolve-button"
              >
              Mark Resolved
              </Button>
          )}
          <Button 
              onClick={() => {
              setViewDialogOpen(false);
              handleDeleteClick(selectedReport);
              }}
              color="error"
              startIcon={<DeleteOutlineIcon />}
          >
              Delete Report
          </Button>
          <Button onClick={() => setViewDialogOpen(false)} variant="outlined">
              Close
          </Button>
          </DialogActions>
      </Dialog>
      
      {/* Resolve Report Dialog */}
      <Dialog
        open={resolveDialogOpen}
        onClose={() => !resolveLoading && setResolveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        data-testid="resolve-dialog"
      >
        <DialogTitle>
          Mark Bug Report as Resolved
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Typography variant="body1" paragraph>
              Add resolution details for bug report: <strong>{selectedReport?.title}</strong>
            </Typography>
            <TextField
              autoFocus
              multiline
              rows={4}
              margin="normal"
              id="resolution"
              name="resolution"
              label="Resolution Details"
              fullWidth
              value={resolution}
              onChange={(e) => setResolution(e.target.value)}
              disabled={resolveLoading}
              placeholder="Please provide details on how this issue was resolved"
              data-testid="resolution-input"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setResolveDialogOpen(false)} 
            disabled={resolveLoading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleResolveSubmit} 
            color="success" 
            variant="contained"
            disabled={resolveLoading}
            startIcon={resolveLoading ? <CircularProgress size={16} /> : <CheckCircleIcon />}
            data-testid="resolve-submit-button"
          >
            {resolveLoading ? 'Saving...' : 'Mark as Resolved'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Report Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleteLoading && setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main', display: 'flex', alignItems: 'center' }}>
            <DeleteOutlineIcon sx={{ mr: 1 }} />
            Delete Bug Report
        </DialogTitle>
        <DialogContent>
            <Box sx={{ mt: 1 }}>
            <Typography variant="body1" paragraph>
                Are you sure you want to delete this bug report?
            </Typography>
            <Typography variant="subtitle1">
                <strong>{selectedReport?.title}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                This action cannot be undone. All information related to this bug report will be permanently deleted.
            </Typography>
            </Box>
        </DialogContent>
        <DialogActions>
            <Button 
            onClick={() => setDeleteDialogOpen(false)} 
            disabled={deleteLoading}
            >
            Cancel
            </Button>
            <Button 
            onClick={handleDeleteSubmit} 
            color="error" 
            variant="contained"
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={16} /> : <DeleteOutlineIcon />}
            >
            {deleteLoading ? 'Deleting...' : 'Delete Report'}
            </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );

  // Conditional rendering based on embedded prop
  return embedded ? (
    <>
      {content}
      {dialogs}
    </>
  ) : (
    <AdminPageWrapper
      title="Bug Reports"
      darkMode={darkMode}
      toggleDarkMode={toggleDarkMode}
      themeVariant={themeVariant}
    >
      {content}
      {dialogs}
    </AdminPageWrapper>
  );
};

export default BugReportsAdmin;